import React, { useState, useCallback, memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useMutation } from '@apollo/client';
import Stack from '@mui/material/Stack';
import FileUploadBox from './FileUploadBox';
import UploadDocumentStatusButton from './UploadDocumentStatusButton';
import { UPLOAD_DOCUMENT_STACK_CLASS_NAME } from '../../theme/constants/stack';
import UPLOAD_INVOICE_DOCUMENTS from '../../graphql/mutations/upload-invoice-documents';
import { useSnackBars } from '../../hooks';

const UploadDocumentButtonOnboarding = memo(({
  invoiceId,
  invoiceDocuments,
  documentType,
  accept,
  boxProps,
  errorMessage,
  className,
}) => {
  const [file, setFile] = useState(null);
  const inputRef = useRef();
  const [hasInvoiceDocuments, setHasInvoiceDocuments] = useState(false);
  const { addAlert } = useSnackBars();
  const existingDocument = [...invoiceDocuments].reverse().find(
    (document) => document.type.code === documentType,
  );
  const documentName = existingDocument?.file?.name;
  useEffect(() => {
    setHasInvoiceDocuments(!!existingDocument);
  }, [existingDocument]);

  const [uploadFiles, { loading: loadingUploadFiles }] = useMutation(
    UPLOAD_INVOICE_DOCUMENTS,
    {
      variables: {
        invoiceId,
        inputUploadFiles: file ? [{ documentType, file }] : [],
      },
      skip: !file,
      onError: () => {
        addAlert({
          id: 'upload-document-error',
          message: errorMessage,
          severity: 'error',
        });
      },
    },
  );

  const handleFileChange = useCallback((event) => {
    setFile(event.target.files[0]);
  }, []);
  const uploadFile = useCallback(() => {
    if (file) {
      uploadFiles();
    }
  }, [file, uploadFiles]);

  const onCancel = useCallback(() => {
    setFile(null);
    setHasInvoiceDocuments(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  return (
    <Stack
      sx={{
        height: { xs: 35, md: 55 },
        maxHeight: '100%',
        width: '100%',
        maxWidth: 180,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
      className={className}
      {...boxProps}
    >

      <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <FileUploadBox
          onFileChange={handleFileChange}
          acceptedFiles={accept}
          boxStyles={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            m: 0,
            border: 'none',
            background: 'transparent',
          }}
          inputRef={inputRef}
        >
          <UploadDocumentStatusButton
            hasInvoiceDocuments={hasInvoiceDocuments}
            documentName={documentName}
            file={file}
            uploadFile={uploadFile}
            onCancel={onCancel}
            loading={loadingUploadFiles}
          />
        </FileUploadBox>
      </Box>
    </Stack>
  );
});

UploadDocumentButtonOnboarding.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  invoiceDocuments: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.shape({
      code: PropTypes.string,
    }),
  })).isRequired,
  documentType: PropTypes.string.isRequired,
  accept: PropTypes.string,
  boxProps: PropTypes.shape({
  }),
  errorMessage: PropTypes.string,
  className: PropTypes.string,
};

UploadDocumentButtonOnboarding.defaultProps = {
  accept: '.pdf',
  boxProps: {},
  errorMessage: 'Error al subir el archivo',
  className: UPLOAD_DOCUMENT_STACK_CLASS_NAME,
};

export default UploadDocumentButtonOnboarding;
